import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { LockIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaUserLock } from "react-icons/fa";
import {
  FORGOT_PASSWORD_URL,
  REGISTER_URL,
} from "../services/api";
import { useUser } from "../context/UserContext";

const LAST_USED_ACCOUNTS_KEY = "novatra.lastLogins";
const MAX_STORED_ACCOUNTS = 3;

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "";
  const [name, domain] = email.split("@");
  if (name.length <= 2) return `${name}*****@${domain}`;
  return `${name.slice(0, 2)}*****@${domain}`;
};

const getDisplayName = (email) => {
  if (!email) return "";
  const username = email.split("@")[0] || "";
  return username.charAt(0).toUpperCase() + username.slice(1);
};

const normalizeCompanySlug = (companyName) =>
  companyName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const readStoredAccounts = () => {
  try {
    const storedAccounts = JSON.parse(
      localStorage.getItem(LAST_USED_ACCOUNTS_KEY) || "[]",
    );

    if (Array.isArray(storedAccounts) && storedAccounts.length > 0) {
      return storedAccounts
        .filter((user) => user?.email)
        .map((user) => ({
          ...user,
          maskedEmail: user.maskedEmail || maskEmail(user.email),
        }))
        .sort((a, b) => (b.lastLoginAt || 0) - (a.lastLoginAt || 0))
        .slice(0, MAX_STORED_ACCOUNTS);
    }

    const legacyEmail = localStorage.getItem("lastUserEmail");
    if (!legacyEmail) return [];

    return [
      {
        id: legacyEmail,
        email: legacyEmail,
        companyName: "",
        maskedEmail: maskEmail(legacyEmail),
        lastLoginAt: Date.now(),
      },
    ];
  } catch {
    return [];
  }
};

const persistStoredAccounts = (accounts) => {
  const normalizedAccounts = accounts
    .filter((user) => user?.email)
    .map((user) => ({
      id: `${user.companyName || "default"}:${user.email}`,
      companyName: user.companyName || "",
      email: user.email,
      maskedEmail: maskEmail(user.email),
      lastLoginAt: user.lastLoginAt || Date.now(),
    }))
    .sort((a, b) => b.lastLoginAt - a.lastLoginAt)
    .slice(0, MAX_STORED_ACCOUNTS);

  localStorage.setItem(
    LAST_USED_ACCOUNTS_KEY,
    JSON.stringify(normalizedAccounts),
  );

  if (normalizedAccounts[0]?.email) {
    localStorage.setItem("lastUserEmail", normalizedAccounts[0].email);
  } else {
    localStorage.removeItem("lastUserEmail");
  }

  return normalizedAccounts;
};

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loginForm, setLoginForm] = useState({
    companyName: "",
    email: "",
    password: "",
  });
  const [registerForm, setRegisterForm] = useState({
    companyName: "",
    name: "",
    email: "",
    phone: "",
    department: "",
    designation: "",
    password: "",
    confirmPassword: "",
  });
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    companyName: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [storedAccounts, setStoredAccounts] = useState(readStoredAccounts);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [useAnotherAccount, setUseAnotherAccount] = useState(false);
  const [showStoredEmail, setShowStoredEmail] = useState(false);
  const passwordInputRef = useRef(null);
  const {
    isOpen: isForgotPasswordOpen,
    onOpen: onForgotPasswordOpen,
    onClose: onForgotPasswordClose,
  } = useDisclosure();
  const toast = useToast();
  const { login } = useUser();
  const navigate = useNavigate();

  const bgMain = useColorModeValue("gray.50", "gray.900");
  const panelBg = useColorModeValue("white", "gray.800");
  const headerTextColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const textColorDark = useColorModeValue("gray.800", "gray.100");
  const labelColor = useColorModeValue("gray.700", "gray.400");
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const inputBgHover = useColorModeValue("white", "gray.600");
  const inputBorderColor = useColorModeValue("gray.200", "gray.600");
  const inputBorderHover = useColorModeValue("gray.400", "gray.500");
  const tabListBg = useColorModeValue("gray.100", "gray.700");
  const tabHoverBg = useColorModeValue("gray.200", "gray.600");
  const dividerColor = useColorModeValue("gray.400", "gray.600");
  const accountBoxBg = useColorModeValue(
    "linear-gradient(180deg, rgba(247,250,255,1) 0%, rgba(255,255,255,1) 100%)",
    "linear-gradient(180deg, rgba(45,55,72,1) 0%, rgba(26,32,44,1) 100%)",
  );
  const accountBoxBorder = useColorModeValue("blue.100", "blue.800");
  const secondaryAccountBg = useColorModeValue("white", "gray.700");
  const secondaryAccountBorder = useColorModeValue("gray.200", "gray.600");
  const tooltipBg = useColorModeValue("gray.700", "gray.400");
  const tooltipColor = useColorModeValue("white", "gray.800");
  const iconColor = useColorModeValue("gray.500", "gray.400");
  const outlineButtonHoverBg = useColorModeValue("gray.50", "gray.700");
  const outlineButtonActiveBg = useColorModeValue("gray.100", "gray.600");
  const modalBg = useColorModeValue("white", "gray.800");
  const inputDisabledBg = useColorModeValue("gray.100", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.500", "gray.500");
  const logoSrc = useColorModeValue(
    "/image_dark_mode.png",
    "/image_dark_mode.png",
  );

  const hasStoredAccounts = storedAccounts.length > 0;
  const primaryAccount = storedAccounts[0] || null;
  const secondaryAccounts = storedAccounts.slice(1, MAX_STORED_ACCOUNTS);
  const showAccountSelector =
    activeTab === 0 &&
    hasStoredAccounts &&
    !useAnotherAccount &&
    !selectedAccount;
  const selectedDisplayAccount = selectedAccount || primaryAccount;

  const handleLoginChange = (e) =>
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });

  const handleRegisterChange = (e) =>
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const handleForgotPasswordChange = (e) =>
    setForgotPasswordForm({
      ...forgotPasswordForm,
      [e.target.name]: e.target.value,
    });

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setUseAnotherAccount(false);
    setShowStoredEmail(false);
    setLoginForm({
      companyName: account.companyName || "",
      email: account.email,
      password: "",
    });
    setActiveTab(0);
    setTimeout(() => passwordInputRef.current?.focus(), 0);
  };

  const handleUseDifferentAccount = () => {
    setLoginForm({ companyName: "", email: "", password: "" });
    setSelectedAccount(null);
    setUseAnotherAccount(true);
    setShowStoredEmail(false);
  };

  const handleClearStoredAccount = (accountId) => {
    const nextAccounts = persistStoredAccounts(
      storedAccounts.filter((account) => account.id !== accountId),
    );
    setStoredAccounts(nextAccounts);
    if (selectedAccount?.id === accountId) {
      setSelectedAccount(null);
      setLoginForm({ companyName: "", email: "", password: "" });
    }
    if (nextAccounts.length === 0) setUseAnotherAccount(false);
  };

  const rememberAccount = (companyName, email) => {
    const accountId = `${companyName || "default"}:${email}`;
    const nextAccounts = persistStoredAccounts([
      {
        id: accountId,
        companyName,
        email,
        lastLoginAt: Date.now(),
      },
      ...storedAccounts.filter((account) => account.id !== accountId),
    ]);
    setStoredAccounts(nextAccounts);
  };

  const handleLoginKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    } else if (e.key === "Escape" && selectedAccount) {
      handleUseDifferentAccount();
    }
  };

  const handleRegisterKeyPress = (e) => {
    if (e.key === "Enter") handleRegister();
  };

  const handleLogin = async () => {
    if (!loginForm.companyName || !loginForm.email || !loginForm.password) {
      return toast({
        title: "Missing Fields",
        description: "Company name, email, and password are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);
    try {
      const result = await login(
        normalizeCompanySlug(loginForm.companyName),
        loginForm.email,
        loginForm.password,
      );

      if (result.success) {
        rememberAccount(loginForm.companyName, loginForm.email);
        toast({
          title: "Login successful",
          description: "Welcome back!",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        navigate("/");
        return;
      }

      toast({
        title: "Login failed",
        description: result.message || "Invalid credentials",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (
      !registerForm.companyName ||
      !registerForm.name ||
      !registerForm.email ||
      !registerForm.password
    ) {
      return toast({
        title: "Missing Fields",
        description: "Company name, full name, email, and password are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      return toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (registerForm.password.length < 6) {
      return toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsRegistering(true);
    try {
      const response = await axios.post(REGISTER_URL, {
        companyName: registerForm.companyName,
        email: registerForm.email,
        name: registerForm.name,
        password: registerForm.password,
        contactNumber: registerForm.phone || undefined,
        department: registerForm.department || undefined,
        designation: registerForm.designation || undefined,
        roles: ["NOVATRA_USER"],
      });

      if (response.data.success) {
        toast({
          title: "Registration successful",
          description: "Your account has been created. Please sign in.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        rememberAccount(registerForm.companyName, registerForm.email);
        setLoginForm({
          companyName: registerForm.companyName,
          email: registerForm.email,
          password: "",
        });
        setRegisterForm({
          companyName: "",
          name: "",
          email: "",
          phone: "",
          department: "",
          designation: "",
          password: "",
          confirmPassword: "",
        });
        setSelectedAccount(null);
        setUseAnotherAccount(true);
        setActiveTab(0);
        return;
      }

      throw new Error(response.data.message || "Registration failed");
    } catch (error) {
      toast({
        title: "Registration failed",
        description:
          error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordForm.companyName || !forgotPasswordForm.email) {
      return toast({
        title: "Missing Fields",
        description: "Company name and email are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsSendingReset(true);
    try {
      const response = await axios.post(FORGOT_PASSWORD_URL, {
        companyName: forgotPasswordForm.companyName,
        email: forgotPasswordForm.email,
      });

      if (response.data.success) {
        toast({
          title: "Reset link sent",
          description: "Check your email for password reset instructions.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setForgotPasswordForm({ companyName: "", email: "" });
        onForgotPasswordClose();
        return;
      }

      throw new Error(response.data.message || "Failed to send reset link");
    } catch (error) {
      toast({
        title: "Reset failed",
        description:
          error.response?.data?.message ||
          error.message ||
          "Failed to send reset link. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const openForgotPassword = () => {
    setForgotPasswordForm({
      companyName:
        selectedDisplayAccount?.companyName || loginForm.companyName || "",
      email: selectedDisplayAccount?.email || loginForm.email || "",
    });
    onForgotPasswordOpen();
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={bgMain}
      p={{ base: 0, md: 4 }}
    >
      <Flex
        width="100%"
        maxW="1100px"
        minH={{ base: "100vh", md: "auto" }}
        boxShadow={{ base: "none", md: "lg" }}
        rounded={{ base: "none", md: "xl" }}
        overflow="hidden"
        bg={panelBg}
        flexDir={{ base: "column", md: "row" }}
      >
        {/* Left Hero */}
        <Flex
          flex={{ base: "none", md: "0 0 42%" }}
          bgGradient="linear(to-b, #073763, #0b4f8a)"
          color="white"
          p={{ base: 8, md: 10 }}
          align="center"
          justify="center"
        >
          <VStack spacing={6} textAlign="center" maxW="320px">
            <Image
              src={logoSrc}
              alt="Novatra logo"
              boxSize={{ base: "100px", md: "140px" }}
              objectFit="contain"
            />
            <Heading fontSize={{ base: "lg", md: "xl" }}>
              Novatra - Bright. Seamless. Secure.
            </Heading>
            <Text fontSize="sm" opacity={0.9}>
              The unified platform for multi-tenant operations, automation, and
              secure payment workflows. Manage your business with confidence.
            </Text>
          </VStack>
        </Flex>

        <Flex
          flex="1"
          direction="column"
          bg={panelBg}
          maxH={{ base: "none", md: "calc(100vh - 64px)" }}
          overflowY="auto"
        >
          <HStack
            justify="flex-end"
            px={{ base: 5, sm: 6 }}
            py={{ base: 4, md: 6 }}
            spacing={4}
          >
            <Text fontSize="sm" color={headerTextColor}>
              Need help?
            </Text>
            <Link
              fontSize="sm"
              color="#0B4885"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              Contact Support
            </Link>
          </HStack>

          <Flex
            flex="1"
            direction="column"
            px={{ base: 5, sm: 6, md: 8, lg: 10 }}
            pb={{ base: 8, md: 10 }}
            justify="flex-start"
            align="center"
          >
            <Box width="100%" maxW="420px">
              <Tabs
                index={activeTab}
                onChange={setActiveTab}
                colorScheme="blue"
                variant="soft-rounded"
                isFitted
              >
                <TabList
                  mb={8}
                  bg={tabListBg}
                  p={1.5}
                  borderRadius="xl"
                  boxShadow="sm"
                >
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: tabHoverBg }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign In
                  </Tab>
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: tabHoverBg }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign Up
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel p={0}>
                    <VStack spacing={7} align="stretch">
                      {showAccountSelector && (
                        <VStack spacing={4} align="stretch">
                          <Tooltip
                            label="This device remembers your recent accounts"
                            hasArrow
                            bg={tooltipBg}
                            color={tooltipColor}
                            placement="top"
                            fontSize="xs"
                          >
                            <Box
                              p={5}
                              borderRadius="16px"
                              border="1px solid"
                              borderColor={accountBoxBorder}
                              bg={accountBoxBg}
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{
                                borderColor: "#0B4885",
                                transform: "translateY(-2px)",
                                boxShadow: "lg",
                              }}
                              _active={{ transform: "scale(0.98)" }}
                              onClick={() =>
                                handleAccountSelect(primaryAccount)
                              }
                            >
                              <HStack spacing={4} align="center">
                                <Avatar
                                  name={primaryAccount.email}
                                  size="md"
                                  bg="#0B4885"
                                  color="white"
                                  fontWeight="bold"
                                />
                                <VStack align="flex-start" spacing={1} flex="1">
                                  <HStack spacing={2}>
                                    <Text
                                      fontSize="md"
                                      fontWeight="700"
                                      color={headingColor}
                                    >
                                      Continue as{" "}
                                      {getDisplayName(primaryAccount.email)}
                                    </Text>
                                    <Icon
                                      as={LockIcon}
                                      boxSize={3.5}
                                      color={iconColor}
                                    />
                                  </HStack>
                                  <Text fontSize="sm" color={textColor}>
                                    {showStoredEmail
                                      ? primaryAccount.email
                                      : primaryAccount.maskedEmail}
                                  </Text>
                                  {primaryAccount.companyName && (
                                    <Text
                                      fontSize="xs"
                                      color={secondaryTextColor}
                                    >
                                      {primaryAccount.companyName}
                                    </Text>
                                  )}
                                </VStack>
                                <HStack spacing={1}>
                                  <IconButton
                                    icon={
                                      showStoredEmail ? (
                                        <ViewOffIcon />
                                      ) : (
                                        <ViewIcon />
                                      )
                                    }
                                    size="sm"
                                    variant="ghost"
                                    aria-label={
                                      showStoredEmail
                                        ? "Hide saved email"
                                        : "Show saved email"
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowStoredEmail((current) => !current);
                                    }}
                                  />
                                  <IconButton
                                    icon={<ViewOffIcon />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Remove saved account"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearStoredAccount(
                                        primaryAccount.id,
                                      );
                                    }}
                                    _hover={{ bg: "red.50", color: "red.600" }}
                                  />
                                </HStack>
                              </HStack>
                            </Box>
                          </Tooltip>

                          {secondaryAccounts.length > 0 && (
                            <VStack spacing={3} align="stretch">
                              {secondaryAccounts.map((account) => (
                                <Box
                                  key={account.id}
                                  p={4}
                                  borderRadius="16px"
                                  border="1px solid"
                                  borderColor={secondaryAccountBorder}
                                  bg={secondaryAccountBg}
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  _hover={{
                                    borderColor: "#0B4885",
                                    boxShadow: "md",
                                    transform: "translateY(-1px)",
                                  }}
                                  _active={{ transform: "scale(0.98)" }}
                                  onClick={() => handleAccountSelect(account)}
                                >
                                  <HStack spacing={4}>
                                    <Avatar
                                      name={account.email}
                                      size="sm"
                                      bg="gray.700"
                                      color="white"
                                      fontWeight="bold"
                                    />
                                    <VStack
                                      align="flex-start"
                                      spacing={0}
                                      flex="1"
                                    >
                                      <Text
                                        fontSize="sm"
                                        fontWeight="600"
                                        color={textColorDark}
                                      >
                                        {getDisplayName(account.email)}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color={secondaryTextColor}
                                      >
                                        {account.maskedEmail}
                                      </Text>
                                    </VStack>
                                    <IconButton
                                      icon={<ViewOffIcon />}
                                      size="sm"
                                      variant="ghost"
                                      aria-label={`Remove saved account ${account.email}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearStoredAccount(account.id);
                                      }}
                                      _hover={{
                                        bg: "red.50",
                                        color: "red.600",
                                      }}
                                    />
                                  </HStack>
                                </Box>
                              ))}
                            </VStack>
                          )}

                          <Link
                            fontSize="sm"
                            color={textColor}
                            fontWeight="500"
                            textAlign="center"
                            onClick={handleUseDifferentAccount}
                            _hover={{
                              color: "#0B4885",
                              textDecoration: "underline",
                            }}
                            cursor="pointer"
                          >
                            Use another account
                          </Link>
                        </VStack>
                      )}

                      <VStack spacing={2} align="flex-start">
                        <HStack spacing={3}>
                          <Icon as={FaUserLock} boxSize={6} color="#0B4885" />
                          <Heading
                            fontSize="3xl"
                            color={headingColor}
                            fontWeight="700"
                          >
                            Welcome Back
                          </Heading>
                        </HStack>
                        <Text fontSize="md" color={textColor}>
                          {selectedAccount
                            ? `Signing in as ${getDisplayName(selectedAccount.email)}`
                            : showAccountSelector
                              ? "Select a saved account to continue faster"
                              : "Sign in to continue to Novatra"}
                        </Text>
                      </VStack>

                      <VStack spacing={5}>
                        <Collapse
                          in={!showAccountSelector}
                          animateOpacity
                          style={{ width: "100%" }}
                        >
                          <VStack spacing={5} align="stretch">
                            <FormControl isRequired>
                              <FormLabel
                                fontSize="sm"
                                fontWeight="600"
                                color={labelColor}
                                mb={2}
                              >
                                Company Name
                              </FormLabel>
                              <Input
                                name="companyName"
                                value={loginForm.companyName}
                                onChange={handleLoginChange}
                                onKeyPress={handleLoginKeyPress}
                                placeholder="e.g. Kaan Corporation"
                                size="lg"
                                bg={selectedAccount ? inputDisabledBg : inputBg}
                                border="1px solid"
                                borderColor={inputBorderColor}
                                _hover={{
                                  borderColor: inputBorderHover,
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                }}
                                _focus={{
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                  borderColor: "#0B4885",
                                  boxShadow: "0 0 0 1px #0B4885",
                                }}
                                borderRadius="lg"
                                isReadOnly={!!selectedAccount}
                              />
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel
                                fontSize="sm"
                                fontWeight="600"
                                color={labelColor}
                                mb={2}
                              >
                                Email Address
                              </FormLabel>
                              <Input
                                name="email"
                                type="email"
                                value={loginForm.email}
                                onChange={handleLoginChange}
                                onKeyPress={handleLoginKeyPress}
                                placeholder="Enter your email"
                                size="lg"
                                bg={selectedAccount ? inputDisabledBg : inputBg}
                                border="1px solid"
                                borderColor={inputBorderColor}
                                _hover={{
                                  borderColor: inputBorderHover,
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                }}
                                _focus={{
                                  bg: selectedAccount
                                    ? inputDisabledBg
                                    : inputBgHover,
                                  borderColor: "#0B4885",
                                  boxShadow: "0 0 0 1px #0B4885",
                                }}
                                borderRadius="lg"
                                isReadOnly={!!selectedAccount}
                              />
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel
                                fontSize="sm"
                                fontWeight="600"
                                color={labelColor}
                                mb={2}
                              >
                                Password
                              </FormLabel>
                              <InputGroup>
                                <Input
                                  ref={passwordInputRef}
                                  type={showLoginPassword ? "text" : "password"}
                                  name="password"
                                  value={loginForm.password}
                                  onChange={handleLoginChange}
                                  onKeyPress={handleLoginKeyPress}
                                  placeholder="Enter your password"
                                  size="lg"
                                  bg={inputBg}
                                  border="1px solid"
                                  borderColor={inputBorderColor}
                                  _hover={{
                                    borderColor: inputBorderHover,
                                    bg: inputBgHover,
                                  }}
                                  _focus={{
                                    bg: inputBgHover,
                                    borderColor: "#0B4885",
                                    boxShadow: "0 0 0 1px #0B4885",
                                  }}
                                  borderRadius="lg"
                                />
                                <InputRightElement width="3rem">
                                  <IconButton
                                    h="1.75rem"
                                    size="sm"
                                    onClick={() =>
                                      setShowLoginPassword(!showLoginPassword)
                                    }
                                    icon={
                                      showLoginPassword ? (
                                        <ViewOffIcon />
                                      ) : (
                                        <ViewIcon />
                                      )
                                    }
                                    aria-label={
                                      showLoginPassword
                                        ? "Hide password"
                                        : "Show password"
                                    }
                                    variant="ghost"
                                  />
                                </InputRightElement>
                              </InputGroup>
                            </FormControl>

                            <HStack justify="flex-end" w="full">
                              <Link
                                fontSize="sm"
                                color="#0B4885"
                                fontWeight="600"
                                _hover={{ textDecoration: "underline" }}
                                cursor="pointer"
                                onClick={openForgotPassword}
                              >
                                Forgot password?
                              </Link>
                            </HStack>

                            {selectedAccount && (
                              <Link
                                fontSize="sm"
                                color={textColor}
                                fontWeight="500"
                                alignSelf="center"
                                onClick={handleUseDifferentAccount}
                                _hover={{
                                  color: "#0B4885",
                                  textDecoration: "underline",
                                }}
                                cursor="pointer"
                              >
                                Use another account
                              </Link>
                            )}

                            <Button
                              bg="#0B4885"
                              color="white"
                              size="lg"
                              width="100%"
                              onClick={handleLogin}
                              isLoading={isLoading}
                              fontSize="md"
                              fontWeight="600"
                              _hover={{
                                bg: "#0a3d6e",
                                transform: "translateY(-1px)",
                                boxShadow: "lg",
                              }}
                              _active={{
                                bg: "#083156",
                                transform: "translateY(0)",
                              }}
                              mt={2}
                              borderRadius="lg"
                              transition="all 0.2s"
                              boxShadow="md"
                            >
                              {selectedAccount ? "Continue" : "Sign In"}
                            </Button>
                          </VStack>
                        </Collapse>

                        <HStack spacing={4} my={1} width="100%">
                          <Divider borderColor={dividerColor} />
                          <Text
                            fontSize="sm"
                            color={secondaryTextColor}
                            fontWeight="500"
                            whiteSpace="nowrap"
                          >
                            SECURE ACCESS
                          </Text>
                          <Divider borderColor={dividerColor} />
                        </HStack>
                      </VStack>
                    </VStack>
                  </TabPanel>

                  <TabPanel p={0}>
                    <VStack spacing={6} align="stretch">
                      <VStack spacing={2} align="flex-start" mb={2}>
                        <Heading
                          fontSize="3xl"
                          fontWeight="700"
                          color={headingColor}
                        >
                          Create Your Account
                        </Heading>
                        <Text fontSize="md" color={textColor}>
                          Start your Novatra workspace today
                        </Text>
                      </VStack>

                      <VStack spacing={5}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Company Name
                          </FormLabel>
                          <Input
                            name="companyName"
                            value={registerForm.companyName}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your company name"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Full Name
                          </FormLabel>
                          <Input
                            name="name"
                            value={registerForm.name}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your full name"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Email Address
                          </FormLabel>
                          <Input
                            name="email"
                            type="email"
                            value={registerForm.email}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your email"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Phone Number
                          </FormLabel>
                          <Input
                            name="phone"
                            type="tel"
                            value={registerForm.phone}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your phone number"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Department
                          </FormLabel>
                          <Input
                            name="department"
                            value={registerForm.department}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="e.g. Sales, HR"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Designation
                          </FormLabel>
                          <Input
                            name="designation"
                            value={registerForm.designation}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="e.g. Manager, Executive"
                            size="lg"
                            bg={inputBg}
                            border="1px solid"
                            borderColor={inputBorderColor}
                            _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                            _focus={{
                              bg: inputBgHover,
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showRegisterPassword ? "text" : "password"}
                              name="password"
                              value={registerForm.password}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Create a password"
                              size="lg"
                              bg={inputBg}
                              border="1px solid"
                              borderColor={inputBorderColor}
                              _hover={{
                                borderColor: inputBorderHover,
                                bg: inputBgHover,
                              }}
                              _focus={{
                                bg: inputBgHover,
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowRegisterPassword(!showRegisterPassword)
                                }
                                icon={
                                  showRegisterPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showRegisterPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                            Confirm Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={registerForm.confirmPassword}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Confirm your password"
                              size="lg"
                              bg={inputBg}
                              border="1px solid"
                              borderColor={inputBorderColor}
                              _hover={{
                                borderColor: inputBorderHover,
                                bg: inputBgHover,
                              }}
                              _focus={{
                                bg: inputBgHover,
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                icon={
                                  showConfirmPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showConfirmPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <Button
                          bg="#0B4885"
                          color="white"
                          size="lg"
                          width="100%"
                          onClick={handleRegister}
                          isLoading={isRegistering}
                          loadingText="Creating account..."
                          fontSize="md"
                          fontWeight="600"
                          _hover={{
                            bg: "#0a3d6e",
                            transform: "translateY(-1px)",
                            boxShadow: "lg",
                          }}
                          _active={{
                            bg: "#083156",
                            transform: "translateY(0)",
                          }}
                          mt={2}
                          borderRadius="lg"
                          transition="all 0.2s"
                          boxShadow="md"
                        >
                          Create Account
                        </Button>
                      </VStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <Modal
        isOpen={isForgotPasswordOpen}
        onClose={onForgotPasswordClose}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4} bg={modalBg}>
          <ModalHeader>
            <VStack align="flex-start" spacing={1}>
              <Heading fontSize="2xl" color={headingColor}>
                Reset Password
              </Heading>
              <Text fontSize="sm" fontWeight="normal" color={textColor}>
                Enter your company and email to receive reset instructions.
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                  Company Name
                </FormLabel>
                <Input
                  name="companyName"
                  value={forgotPasswordForm.companyName}
                  onChange={handleForgotPasswordChange}
                  placeholder="Enter your company name"
                  size="lg"
                  bg={inputBg}
                  color={textColorDark}
                  border="1px solid"
                  borderColor={inputBorderColor}
                  _placeholder={{ color: secondaryTextColor }}
                  _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                  _focus={{
                    bg: inputBgHover,
                    borderColor: "#0B4885",
                    boxShadow: "0 0 0 1px #0B4885",
                  }}
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" color={labelColor}>
                  Email Address
                </FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={forgotPasswordForm.email}
                  onChange={handleForgotPasswordChange}
                  placeholder="Enter your email"
                  size="lg"
                  bg={inputBg}
                  color={textColorDark}
                  border="1px solid"
                  borderColor={inputBorderColor}
                  _placeholder={{ color: secondaryTextColor }}
                  _hover={{ borderColor: inputBorderHover, bg: inputBgHover }}
                  _focus={{
                    bg: inputBgHover,
                    borderColor: "#0B4885",
                    boxShadow: "0 0 0 1px #0B4885",
                  }}
                  borderRadius="lg"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleForgotPassword();
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={onForgotPasswordClose}
              size="lg"
              fontWeight="600"
              _hover={{ bg: outlineButtonActiveBg }}
            >
              Cancel
            </Button>
            <Button
              bg="#0B4885"
              color="white"
              size="lg"
              onClick={handleForgotPassword}
              isLoading={isSendingReset}
              loadingText="Sending..."
              fontWeight="600"
              _hover={{
                bg: "#0a3d6e",
                transform: "translateY(-1px)",
                boxShadow: "lg",
              }}
              _active={{
                bg: "#083156",
                transform: "translateY(0)",
              }}
              borderRadius="lg"
              transition="all 0.2s"
            >
              Send Reset Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default LoginPage;
